// deployment_class: customer_env_allowed
/**
 * One top-bar menu open at a time.
 *
 * Each menu previously owned an isolated `open` boolean, so opening one left the
 * others standing — two panels overlapping, and `UserMenu` never retracted at
 * all because it had no outside-click handler.
 *
 * Fixing that pairwise (teach A about B) grows quadratically and the next menu
 * added reintroduces it. Instead the OPEN MENU IS A SINGLE VALUE: a module-level
 * id with subscribers. Opening one sets the id, which closes every other by
 * construction — a menu cannot fail to notice a sibling it does not know about.
 *
 * Also centralises Escape and outside-click, so no menu can ship without them.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

let openMenuId: string | null = null;
const subscribers = new Set<() => void>();

function setOpenMenu(id: string | null): void {
  if (openMenuId === id) return;
  openMenuId = id;
  for (const notify of subscribers) notify();
}

export interface ExclusiveMenu {
  open: boolean;
  toggle: () => void;
  close: () => void;
  /** Put on the menu's outermost element — outside-click is measured from it. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Put on the trigger, so focus returns there on Escape. */
  triggerRef: React.RefObject<HTMLButtonElement>;
}

/**
 * @param id stable per menu (e.g. 'project', 'portal', 'user').
 */
export function useExclusiveMenu(id: string): ExclusiveMenu {
  const [, force] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const notify = (): void => force((n) => n + 1);
    subscribers.add(notify);
    return () => {
      subscribers.delete(notify);
      // A menu that unmounts while open must not leave the slot claimed, or
      // every other menu stays shut.
      if (openMenuId === id) setOpenMenu(null);
    };
  }, [id]);

  const open = openMenuId === id;

  const close = useCallback(() => {
    if (openMenuId === id) setOpenMenu(null);
  }, [id]);

  const toggle = useCallback(() => {
    setOpenMenu(openMenuId === id ? null : id);
  }, [id]);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      setOpenMenu(null);
      triggerRef.current?.focus();
    }
    function onPointerDown(e: MouseEvent): void {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpenMenu(null);
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  return { open, toggle, close, containerRef, triggerRef };
}

/** Test-only reset — module state would otherwise leak between cases. */
export function __resetExclusiveMenusForTesting(): void {
  openMenuId = null;
  subscribers.clear();
}
